/**
 * CustomNode — port of the real Nemo CustomNode
 * (apps/frontend-next/src/ui/views/layout/network/production-editor/flow/custom-nodes/index.tsx).
 *
 * Same visual contract:
 *   - white card, 1px solid grey-300 border, rounded-md (6px)
 *   - on hover/select: floating action bar -36px above the card
 *   - icon (SVG) centered, label above & below
 *   - Handle invisible (we render edges, not connection dots)
 *
 * Differences vs real impl:
 *   - No i18n (prototype layer) — pass already-translated strings.
 *   - No `data-toggle="modal"` Bootstrap pattern — pass `onEditClick` instead.
 *   - NodeResizeControl shown for all non-readonly nodes (matches real app).
 *
 * To use directly inside a NodeTypes map:
 *   `{ asset: AssetNode, contract: Contract, group: Group, groupMember: GroupMember }`.
 */
import {
    NJIcon,
    NJTooltip,
} from '@engie-group/fluid-design-system-react';
import {
    Handle,
    NodeResizeControl,
    Position,
} from '@xyflow/react';
import type { ReactNode } from 'react';
import { READ_ONLY_NODE_TYPES, getNodeIcon } from './icons';

export interface CustomNodeProps {
    id: string;
    entityName: string;
    /** e.g. 'Boiler', 'Variable speed pump', 'Fuel contract'. */
    entityLabel: string;
    /** User-facing instance name. */
    label: string;
    selected?: boolean;
    /** Where Handles are placed. Defaults to [Left, Right]. */
    handles?: Position[];
    /** Called when the user clicks the Edit action in the floating bar. */
    onEditClick?: () => void;
    /** Called when the user clicks the Delete action. */
    onDeleteClick?: () => void;
    /** Called when the user clicks the Duplicate action. */
    onDuplicateClick?: () => void;
    /** Optional extra actions in the floating bar. */
    extraActions?: ReactNode;
    /** Chiller type for color differentiation. */
    chillerType?: 'Centrifuge' | 'Multi-centrifuge' | 'Scroll';
}

export default function CustomNode(props: CustomNodeProps) {
    const {
        id,
        entityName,
        entityLabel,
        label,
        selected,
        handles = [Position.Left, Position.Right],
        onEditClick,
        onDeleteClick,
        onDuplicateClick,
        extraActions,
        chillerType,
    } = props;

    const isReadOnly = READ_ONLY_NODE_TYPES.includes(entityName);
    const icon = getNodeIcon(entityName);

    if (!entityName) {
        throw new Error('Entity name is required');
    }

    // Chiller icon background color based on type
    let iconBackgroundColor = 'white';
    if (entityName === 'Chiller' && chillerType) {
        switch (chillerType) {
            case 'Centrifuge':
                iconBackgroundColor = 'rgba(25, 118, 210, 0.08)'; // Bleu très léger
                break;
            case 'Multi-centrifuge':
                iconBackgroundColor = 'rgba(156, 39, 176, 0.08)'; // Violet très léger
                break;
            case 'Scroll':
                iconBackgroundColor = 'rgba(0, 150, 136, 0.08)'; // Cyan/Teal très léger
                break;
        }
    }

    return (
        <div
            data-testid={`custom-node-${id}`}
            style={{
                position: 'relative',
                height: '100%',
                padding: 16,
                background: 'white',
                border: '1px solid var(--nj-core-color-grey-300)',
                borderRadius: '6px',
                cursor: isReadOnly ? 'grab' : 'pointer',
                fontFamily: 'var(--nj-core-font-family-sans)',
                boxSizing: 'border-box',
                outline: selected
                    ? '2px solid var(--nj-semantic-color-border-brand-strong-default)'
                    : 'none',
                outlineOffset: 1,
            }}
        >
            {!isReadOnly && selected && (onEditClick || onDeleteClick || onDuplicateClick || extraActions) && (
                <div
                    style={{
                        position: 'absolute',
                        top: -36,
                        left: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: 4,
                        background: 'white',
                        border: '1px solid var(--nj-core-color-grey-300)',
                        borderRadius: '6px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        zIndex: 10,
                        width: 'max-content',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {onEditClick && (
                        <NJTooltip text="Modifier" isColorInverse={false} hasArrow placement="top">
                            <button
                                type="button"
                                onClick={onEditClick}
                                aria-label="Modifier"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: 0,
                                    background: 'transparent',
                                }}
                            >
                                <NJIcon name="edit" />
                            </button>
                        </NJTooltip>
                    )}
                    {onDuplicateClick && (
                        <NJTooltip text="Dupliquer" isColorInverse={false} hasArrow placement="top">
                            <button
                                type="button"
                                onClick={onDuplicateClick}
                                aria-label="Dupliquer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: 0,
                                    background: 'transparent',
                                }}
                            >
                                <NJIcon name="content_copy" />
                            </button>
                        </NJTooltip>
                    )}
                    {onDeleteClick && (
                        <NJTooltip text="Supprimer" isColorInverse={false} hasArrow placement="top">
                            <button
                                type="button"
                                onClick={onDeleteClick}
                                aria-label="Supprimer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 4,
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    border: 0,
                                    background: 'transparent',
                                }}
                            >
                                <NJIcon name="delete" />
                            </button>
                        </NJTooltip>
                    )}
                    {extraActions}
                </div>
            )}

            {handles.map((handle) => (
                <Handle
                    data-testid={`source-${handle}`}
                    key={`source-${handle}`}
                    type="source"
                    position={handle}
                    id={handle}
                />
            ))}
            {/* Additional custom handles for specific node types */}
            {entityName === 'StorageTank' && (
                <>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="s-right"
                        style={{ right: '50%', bottom: 0, top: 'auto', transform: 'translateX(50%)' }}
                    />
                    <Handle
                        type="target"
                        position={Position.Right}
                        id="s-right"
                        style={{ right: '50%', bottom: 0, top: 'auto', transform: 'translateX(50%)' }}
                    />
                </>
            )}

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    minWidth: 0,
                }}
            >
                {isReadOnly ? (
                    <span style={{ fontSize: 12, lineHeight: 1.3 }}>{entityLabel}</span>
                ) : (
                    <>
                        <span style={{ fontSize: 12, lineHeight: 1.3, fontWeight: 600 }}>{label}</span>
                        {icon && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 52,
                                    height: 52,
                                    margin: '8px 0',
                                    borderRadius: '6px',
                                    background: iconBackgroundColor,
                                    border: '1px solid var(--nj-core-color-grey-300)',
                                }}
                            >
                                <img
                                    src={icon}
                                    alt={label}
                                    width={36}
                                    height={36}
                                />
                            </div>
                        )}
                        <span style={{ fontSize: 11, lineHeight: 1.3, color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>{entityLabel}</span>
                    </>
                )}
            </div>

            {handles.map((handle) => (
                <Handle
                    data-testid={`target-${handle}`}
                    key={`target-${handle}`}
                    type="target"
                    position={handle}
                    id={handle}
                />
            ))}

            {!isReadOnly && <NodeResizeControl minWidth={80} minHeight={60} />}
        </div>
    );
}
